import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogTitle, findSave } from "./ui/dialog";
import { useAuth } from "../context/AuthContext";
import { startTour } from "../lib/tour";
import { requestAddPlant } from "./NavBar";

const SHORTCUTS = [
  ["/", "Search the collection"],
  ["N", "Add a plant"],
  ["C", "Log care"],
  ["S", "Scan a label"],
  ["T", "Take the tour"],
  ["?", "Show these shortcuts"],
];
const EDITING = [
  ["Enter", "Save a one-line field"],
  ["⌘/Ctrl + Enter", "Save a note"],
  ["Esc", "Cancel an edit or close a window"],
];
const Kbd = ({ k }) => <kbd className="min-w-7 h-7 px-2 rounded-lg bg-muted text-xs font-semibold inline-flex items-center justify-center shadow-[inset_0_-1px_0_rgba(0,0,0,0.08)]">{k}</kbd>;

const typing = (t) => t instanceof HTMLElement && (t.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName));
const layerOpen = () => !!document.querySelector('[role="dialog"][data-state="open"], [role="menu"], [data-testid="tour-overlay"], [data-testid="scan-dialog"], [data-testid="care-round"]');
const clickFirst = (...ids) => { const el = ids.map((id) => document.querySelector(`[data-testid="${id}"]`)).find((x) => x && x.offsetParent !== null); el?.click(); return !!el; };

// Enter / Cmd+Enter for fields that live directly on a page (dialogs handle their own).
function pageSave(e) {
  const t = e.target;
  if (e.key !== "Enter" || e.isComposing || e.defaultPrevented || t.closest?.('[role="dialog"], [data-radix-popper-content-wrapper]')) return;
  const area = t instanceof HTMLTextAreaElement;
  if (area ? !(e.metaKey || e.ctrlKey) : e.shiftKey || !(t instanceof HTMLInputElement) || ["checkbox", "radio", "file", "button", "submit", "search"].includes(t.type)) return;
  const form = t.closest("form");
  if (form) { if (area) { e.preventDefault(); form.requestSubmit(); } return; }
  let scope = t.parentElement;
  for (let d = 0; scope && d < 4; d++, scope = scope.parentElement) {
    const btn = findSave(scope);
    if (btn) { e.preventDefault(); btn.click(); return; }
  }
}

export default function GlobalKeys() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [help, setHelp] = useState(false);

  useEffect(() => {
    if (!user) return undefined;
    const home = (fn) => { if (pathname === "/") fn(); else { navigate("/"); setTimeout(fn, 450); } };
    const onKey = (e) => {
      pageSave(e);
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || typing(e.target) || layerOpen()) return;
      const k = e.key.toLowerCase();
      const run = {
        "/": () => home(() => { const s = document.querySelector('[data-testid="plant-search-input"]'); s?.focus(); s?.scrollIntoView({ block: "center", behavior: "smooth" }); }),
        n: () => requestAddPlant(navigate),
        c: () => home(() => clickFirst("quick-action-care")),
        s: () => home(() => clickFirst("search-scan-btn", "quick-action-scan")),
        t: () => startTour(),
        "?": () => setHelp(true),
      }[e.key === "?" ? "?" : k];
      if (run) { e.preventDefault(); run(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [user, pathname, navigate]);

  return (
    <Dialog open={help} onOpenChange={setHelp}>
      <DialogContent className="max-w-sm rounded-3xl" data-testid="shortcuts-dialog">
        <DialogTitle className="text-xl font-semibold tracking-tight">Keyboard shortcuts</DialogTitle>
        <DialogDescription className="-mt-2">Work faster on a computer.</DialogDescription>
        {[["Anywhere", SHORTCUTS], ["While editing", EDITING]].map(([title, rows]) => (
          <div key={title}>
            <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5">{title}</p>
            <ul className="divide-y divide-border/60">
              {rows.map(([k, label]) => <li key={k} className="flex items-center justify-between py-2 text-sm">{label}<Kbd k={k} /></li>)}
            </ul>
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
}
